// Reusable brand + date filtering for the "system" header filters (Chat, CRM,
// Email, …). Mirrors the brand scoping the Automation Pipeline already has and
// adds a date filter with the standard presets — Today, Last week, Last month,
// Custom — plus an "All time" passthrough default so a page keeps showing
// everything until the operator narrows it.
//
// All logic lives here as pure functions so it can be unit-tested without a DOM
// and shared by every page through <SystemFilterBar />.

const DAY_MS = 24 * 60 * 60 * 1000;

// Date-filter presets surfaced in the header dropdown, in display order. `all`
// is the default and applies no date bound; `custom` defers to an explicit
// [from, to] range chosen in the range picker.
export const DATE_PRESETS = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "week", label: "Last week" },
  { key: "month", label: "Last month" },
  { key: "custom", label: "Custom" },
];

export const DEFAULT_DATE_PRESET = "all";

// The default empty filter state a page seeds into useState.
export const EMPTY_SYSTEM_FILTERS = {
  brandId: undefined,
  datePreset: DEFAULT_DATE_PRESET,
  customRange: null,
};

// Fields we look at, in priority order, when pulling a timestamp off an item.
// Different systems stamp their rows differently (chat → lastMessageAt,
// crm → lastInboundAt, email → receivedAt, newsletters → scheduledFor, …) so we
// accept any of them and use the first one present.
const DATE_FIELDS = [
  "lastMessageAt",
  "lastInboundAt",
  "receivedAt",
  "sentAt",
  "scheduledFor",
  "nextDueAt",
  "date",
  "createdAt",
  "updatedAt",
];

// Brand reference fields, tolerating both snake/camel naming and a populated
// brand sub-document.
const BRAND_FIELDS = ["brandId", "brand_id", "brand"];

// Coerce a value (number ms, ISO/parseable string, Date, or moment-like) to ms.
function toMs(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  if (typeof v === "object") {
    // moment objects and Dates both expose a numeric valueOf().
    if (typeof v.valueOf === "function") {
      const n = v.valueOf();
      return typeof n === "number" && !Number.isNaN(n) ? n : null;
    }
    return null;
  }
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

function startOfDay(ms) {
  if (ms == null) return null;
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function endOfDay(ms) {
  if (ms == null) return null;
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
}

// Extract a comparable timestamp (ms) from an item, or null if none present.
export function itemDate(item, fields = DATE_FIELDS) {
  if (!item) return null;
  for (const f of fields) {
    if (item[f] != null) {
      const ms = toMs(item[f]);
      if (ms != null) return ms;
    }
  }
  return null;
}

// Extract a brand id string from an item, tolerating populated objects and the
// various field names used across collections. null if absent.
export function itemBrandId(item, fields = BRAND_FIELDS) {
  if (!item) return null;
  for (const f of fields) {
    const v = item[f];
    if (v == null) continue;
    if (typeof v === "object") {
      const id = v._id || v.id;
      if (id != null) return String(id);
    } else {
      return String(v);
    }
  }
  return null;
}

// Resolve a preset (+ optional custom range) into an inclusive [fromMs, toMs]
// window. `null` on either bound means unbounded on that side. `now` is
// injectable so callers and tests are deterministic.
export function dateRangeForPreset(preset, now = Date.now(), customRange = null) {
  const ref = toMs(now);
  const refMs = ref == null ? Date.now() : ref;
  switch (preset) {
    case "today":
      return { fromMs: startOfDay(refMs), toMs: endOfDay(refMs) };
    case "week":
      return { fromMs: refMs - 7 * DAY_MS, toMs: refMs };
    case "month":
      return { fromMs: refMs - 30 * DAY_MS, toMs: refMs };
    case "custom": {
      const [from, to] = customRange || [];
      return {
        fromMs: from != null ? startOfDay(toMs(from)) : null,
        toMs: to != null ? endOfDay(toMs(to)) : null,
      };
    }
    case "all":
    default:
      return { fromMs: null, toMs: null };
  }
}

// Filter a list of items by brand + date according to the header filter state.
//
//   filters: { brandId, datePreset, customRange: [from, to] }
//   opts:    { now, dateFields, brandFields } — all optional, for tests/overrides
//
// A brand selection keeps only items whose brand id matches. A date preset other
// than "all" keeps only items whose timestamp falls inside the window; items
// with no recognisable date are dropped when a date filter is active.
export function applySystemFilter(items = [], filters = {}, opts = {}) {
  const {
    brandId,
    datePreset = DEFAULT_DATE_PRESET,
    customRange = null,
  } = filters;
  const { now = Date.now(), dateFields, brandFields } = opts;
  const { fromMs, toMs } = dateRangeForPreset(datePreset, now, customRange);
  const wantBrand = brandId != null && brandId !== "";
  const wantDate = fromMs != null || toMs != null;

  return (items || []).filter((it) => {
    if (wantBrand && itemBrandId(it, brandFields) !== String(brandId)) return false;
    if (wantDate) {
      const ts = itemDate(it, dateFields);
      if (ts == null) return false;
      if (fromMs != null && ts < fromMs) return false;
      if (toMs != null && ts > toMs) return false;
    }
    return true;
  });
}
